import { useTranslation } from "react-i18next";
import {
  Checkbox as MuiCheckbox,
  OutlinedInput as MuiOutlinedInput,
  InputLabel as MuiInputLabel,
  Select as MuiSelect,
  MenuItem as MuiMenuItem,
  Grid as MuiGrid,
  TextField as MuiTextField,
  Typography as MuiTypography,
  FormControl as MuiFormControl,
  FormControlLabel as MuiFormControlLabel,
  FormHelperText as MuiFormHelperText,
  Chip as MuiChip,
  Stack as MuiStack,
  Switch as MuiSwitch,
} from "@mui/material";
import { useFormikContext } from "formik";
import {
  StudyEntities,
  StudyChannelOptions,
  RecruitmentPreferences,
} from "app/shared/constants";
import { getPrefByName } from "app/shared/utils";
import { createEditStudyFormStyles } from "app/components/study/edit/CreateEditStudyFormStyles";
import { switchStyles } from "app/shared/UI/sharedStyles";

const StudyPreferencesTab = () => {
  const {
    values,
    setFieldTouched,
    setFieldValue,
    errors,
    touched,
    handleBlur,
  } = useFormikContext();
  const { t } = useTranslation();

  // checks if there are any errors in the recruitment preferences(preference tab fields) for a specific preference index.
  const hasRecruitmentPreferencesErrors = (prefIndex) =>
    Object.keys(errors).length > 0 &&
    Object.keys(touched).length > 0 &&
    touched?.recruitmentPreferences &&
    touched?.recruitmentPreferences[prefIndex] &&
    errors?.recruitmentPreferences &&
    errors?.recruitmentPreferences[prefIndex]
      ? true
      : false;

  return (
    <MuiGrid container justifyContent="space-between" spacing={2}>
      <MuiGrid size={6}>
        <MuiFormControl
          fullWidth
          variant="outlined"
          error={hasRecruitmentPreferencesErrors(0)}
        >
          <MuiInputLabel
            htmlFor="recruitmentPreferences[0].value"
            shrink={true}
          >
            {t("recruitmentPreference." + RecruitmentPreferences[0])}
          </MuiInputLabel>
          <MuiSelect
            multiple
            required
            size="small"
            input={
              <MuiOutlinedInput
                id="recruitmentPreferences[0].value"
                name="recruitmentPreferences[0].value"
                label={t("recruitmentPreference." + RecruitmentPreferences[0])}
                margin="dense"
                notched
                onBlur={handleBlur}
                onChange={(event, value) => {
                  setFieldTouched("recruitmentPreferences[0].value", true);
                  setFieldValue(
                    "recruitmentPreferences[0].value",
                    event.target.value.toString(),
                  );
                }}
              />
            }
            value={
              getPrefByName(values, RecruitmentPreferences[0]) &&
              getPrefByName(values, RecruitmentPreferences[0]).value
                ? getPrefByName(values, RecruitmentPreferences[0]).value.split(
                    ",",
                  )
                : []
            }
            renderValue={(selected) => (
              <div style={createEditStudyFormStyles.chipContainer}>
                {selected.map((value) => (
                  <MuiChip
                    sx={createEditStudyFormStyles.chip}
                    key={value}
                    label={t("recruitmentPreference." + value)}
                  />
                ))}
              </div>
            )}
          >
            {Object.keys(StudyChannelOptions).map((channelOption) => (
              <MuiMenuItem key={channelOption} value={channelOption}>
                {t("recruitmentPreference." + channelOption)}
              </MuiMenuItem>
            ))}
          </MuiSelect>
          {hasRecruitmentPreferencesErrors(0) && (
            <MuiFormHelperText focused={true}>
              {t("formValidation.requiredField")}
            </MuiFormHelperText>
          )}
        </MuiFormControl>
      </MuiGrid>
      <MuiGrid size={6}>
        <MuiFormControl
          fullWidth
          variant="outlined"
          error={hasRecruitmentPreferencesErrors(1)}
        >
          <MuiInputLabel
            htmlFor="recruitmentPreferences[1].value"
            shrink={true}
          >
            {t("recruitmentPreference." + RecruitmentPreferences[1])}
          </MuiInputLabel>
          <MuiSelect
            multiple
            required
            size="small"
            input={
              <MuiOutlinedInput
                id="recruitmentPreferences[1].value"
                name="recruitmentPreferences[1].value"
                label={t("recruitmentPreference." + RecruitmentPreferences[1])}
                margin="dense"
                size="small"
                notched
                onBlur={handleBlur}
                onChange={(event, value) => {
                  setFieldTouched("recruitmentPreferences[1].value", true);
                  setFieldValue(
                    "recruitmentPreferences[1].value",
                    event.target.value.toString(),
                  );
                }}
              />
            }
            value={
              getPrefByName(values, RecruitmentPreferences[1]) &&
              getPrefByName(values, RecruitmentPreferences[1]).value
                ? getPrefByName(values, RecruitmentPreferences[1]).value.split(
                    ",",
                  )
                : []
            }
            renderValue={(selected) => (
              <div style={createEditStudyFormStyles.chipContainer}>
                {selected.map((value) => (
                  <MuiChip
                    sx={createEditStudyFormStyles.chip}
                    key={value}
                    label={value}
                  />
                ))}
              </div>
            )}
          >
            {StudyEntities.map((entityItem) => (
              <MuiMenuItem key={entityItem} value={entityItem}>
                {entityItem}
              </MuiMenuItem>
            ))}
          </MuiSelect>
          {hasRecruitmentPreferencesErrors(1) && (
            <MuiFormHelperText focused={true}>
              {t("formValidation.requiredField")}
            </MuiFormHelperText>
          )}
        </MuiFormControl>
      </MuiGrid>
      <MuiGrid size={3}>
        <MuiFormControl fullWidth variant="outlined">
          <MuiTextField
            fullWidth
            id="recruitmentPreferences[2].value"
            name="recruitmentPreferences[2].value"
            label={t("recruitmentPreference." + RecruitmentPreferences[2])}
            variant="outlined"
            margin="dense"
            size="small"
            onBlur={handleBlur}
            onChange={(event, value) => {
              setFieldTouched("recruitmentPreferences[2].value", true);
              setFieldValue(
                "recruitmentPreferences[2].value",
                event.target.value.toString(),
              );
            }}
            value={
              getPrefByName(values, RecruitmentPreferences[2])
                ? getPrefByName(values, RecruitmentPreferences[2]).value
                : 0
            }
            sx={createEditStudyFormStyles.space}
            required={true}
            error={hasRecruitmentPreferencesErrors(2)}
            helperText={
              hasRecruitmentPreferencesErrors(2)
                ? t("formValidation.requiredField")
                : null
            }
          />
        </MuiFormControl>
      </MuiGrid>
      <MuiGrid size={3}>
        <MuiFormControl fullWidth variant="outlined">
          <MuiTextField
            fullWidth
            id="recruitmentPreferences[3].value"
            name="recruitmentPreferences[3].value"
            label={t("recruitmentPreference." + RecruitmentPreferences[3])}
            variant="outlined"
            margin="dense"
            size="small"
            onBlur={handleBlur}
            onChange={(event, value) => {
              setFieldTouched("recruitmentPreferences[3].value", true);
              setFieldValue(
                "recruitmentPreferences[3].value",
                event.target.value.toString(),
              );
            }}
            value={
              getPrefByName(values, RecruitmentPreferences[3])
                ? getPrefByName(values, RecruitmentPreferences[3]).value
                : 0
            }
            sx={createEditStudyFormStyles.space}
            required={true}
            error={hasRecruitmentPreferencesErrors(3)}
            helperText={
              hasRecruitmentPreferencesErrors(3)
                ? t("formValidation.requiredField")
                : null
            }
          />
        </MuiFormControl>
      </MuiGrid>
      <MuiGrid size={6}>
        <MuiFormControl fullWidth>
          <MuiFormControlLabel
            fullWidthLabel
            control={
              <MuiCheckbox
                checked={
                  getPrefByName(values, RecruitmentPreferences[4]) &&
                  getPrefByName(values, RecruitmentPreferences[4]).value ===
                    "true"
                    ? true
                    : false
                }
                color="pepGray"
                size="small"
                onChange={(evt, newValue) => {
                  setFieldTouched("recruitmentPreferences[4].value", true);
                  setFieldValue(
                    "recruitmentPreferences[4].value",
                    newValue.toString(),
                  );
                }}
              />
            }
            name="prefIncludeUndecided"
            label={t("recruitmentPreference." + RecruitmentPreferences[4])}
          />
        </MuiFormControl>
      </MuiGrid>
      <MuiGrid size={6}>
        <MuiFormControl fullWidth variant="outlined">
          <MuiTextField
            fullWidth
            id="recruitmentPreferences[5].value"
            name="recruitmentPreferences[5].value"
            label={t("recruitmentPreference." + RecruitmentPreferences[5])}
            variant="outlined"
            margin="dense"
            onBlur={handleBlur}
            size="small"
            onChange={(evt) => {
              const value = evt.target.value;

              // Update the field value regardless of validation outcome
              setFieldTouched("recruitmentPreferences[5].value", true);
              setFieldValue(
                "recruitmentPreferences[5].value",
                value.toString(),
              );
            }}
            value={
              getPrefByName(values, RecruitmentPreferences[5])
                ? getPrefByName(values, RecruitmentPreferences[5]).value
                : ""
            }
            sx={createEditStudyFormStyles.space}
            error={hasRecruitmentPreferencesErrors(5)}
            helperText={
              hasRecruitmentPreferencesErrors(5)
                ? errors?.recruitmentPreferences[5].value ===
                  t("formValidation.preferenceOnboardingCapacity")
                  ? t("formValidation.preferenceOnboardingCapacity")
                  : t("formValidation.requiredField")
                : null
            }
          />
        </MuiFormControl>
        <MuiFormControl sx={{ marginTop: "1em" }}>
          <MuiTypography>
            {t(`recruitmentPreference.${RecruitmentPreferences[8]}`)}
          </MuiTypography>
          <MuiStack alignItems="center" direction="row" spacing={1}>
            <MuiTypography>{t("common.noLabel")}</MuiTypography>
            <MuiSwitch
              sx={switchStyles}
              name="isSelfServiceEnabled"
              checked={
                getPrefByName(values, RecruitmentPreferences[8]) &&
                getPrefByName(values, RecruitmentPreferences[8]).value ===
                  "true"
                  ? true
                  : false
              }
              onChange={(evt, newValue) => {
                setFieldTouched("recruitmentPreferences[8].value", true);
                setFieldValue(
                  "recruitmentPreferences[8].value",
                  newValue.toString(),
                );
              }}
            />
            <MuiTypography>{t("common.yesLabel")}</MuiTypography>
          </MuiStack>
        </MuiFormControl>
      </MuiGrid>
      <MuiGrid size={6}>
        <MuiFormControl
          fullWidth
          variant="outlined"
          sx={createEditStudyFormStyles.areaZipCodes}
        >
          <MuiTextField
            fullWidth
            multiline
            rows={5}
            name="prefIncludeZipCode"
            label={t(`recruitmentPreference.${RecruitmentPreferences[6]}`)}
            variant="outlined"
            margin="dense"
            size="small"
            onChange={(e) => {
              setFieldTouched("recruitmentPreferences[6].value", true);
              setFieldValue(
                "recruitmentPreferences[6].value",
                e.target.value.toString(),
              );
            }}
            value={
              getPrefByName(values, RecruitmentPreferences[6])
                ? getPrefByName(values, RecruitmentPreferences[6]).value
                : ""
            }
          />
        </MuiFormControl>
      </MuiGrid>
    </MuiGrid>
  );
};

export default StudyPreferencesTab;
